import React, {
  Context,
  createContext,
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useContext,
  useState,
} from "react";
import { UserStorageKey } from "../userStorage/userStorageKey";
import {
  UserStorageKeyType,
  useUserStorage,
} from "../userStorage/useUserStorage";

export type StateVar<S> = [S, Dispatch<SetStateAction<S>>];

const contextProvider = <T extends {}>(
  context: Context<StateVar<T>>,
  initial?: T,
  localStoreKey?: UserStorageKey,
  initializer: () => T = null,
) => ({ value, children }: { value: T; children?: any }) => {
  const ContextProvider = context.Provider;
  let state = localStoreKey
    ? useStateWithLocalStorage(localStoreKey, value || initial, initializer)
    : useState(value || initial || (initializer && initializer()));
  return <ContextProvider value={state}>{children}</ContextProvider>;
};

/**
 * should be called outside of the react tree or at a top level component
 * example:
 *     const [ProviderSomeData, useSomeData] = createUpdatableContext<SomeData>();
 *
 *     <ProviderSomeData value={initial}>
 *     ...
 *
 *     const [someData, setSomeData] = useSomeData();
 */
export function createUpdatableContext<T>(
  initial?: T,
  initializer: () => T = null,
  localStoreKey: UserStorageKey = null,
): [FunctionComponent<{ value?: T, children: any }>, () => StateVar<T>] {
  // create a unique context for this object
  const theContext: Context<StateVar<T>> = createContext<StateVar<T>>(null);

  //  create a custom hook to retrieve the context object
  const use = () => useContext(theContext) || [null, null];

  // create a wrapped provider component
  const provide = contextProvider(
    theContext,
    initial,
    localStoreKey,
    initializer,
  );

  return [provide, use];
}

function useStateWithLocalStorage<T>(
  localStorageKey: UserStorageKey | null,
  initialValue: T,
  initializer: () => T = null,
) {
  const { writeToStorage, readFromStorage } = useUserStorage();

  const initial = initialValue || (initializer && initializer());

  const [value, setValue] = React.useState<T>(
    readFromStorage(
      localStorageKey /*,UserStorageKeyType.Session JM- looks wrong*/,
    ) || initial,
  );

  React.useEffect(() => {
    if (localStorageKey && false) {
      writeToStorage(localStorageKey, value ? JSON.stringify(value) : null);
    }
  }, [value]);

  return [value, setValue] as [T, typeof setValue];
}
